import React, { useMemo } from 'react';

import { useIntl } from 'react-intl';

import { fg } from '@atlaskit/platform-feature-flags';
import { Stack, Text } from '@atlaskit/primitives/compiled';
import type { AgentScenarioAction } from '@atlassian/conversation-assistant-service-api/types/assistance-service';

import {
	AgentActionPill,
	ToolPill,
} from '../../../../../../common/ui/agent-actions/agent-action-pill/index.tsx';
import { type AgentDetailsWithAggData } from '../../../../../../controllers/agent-details/index.ts';

import messages from './messages.ts';

function deduplicateById<T extends { id: string }>(items: T[] | undefined): T[] {
	return Array.from(new Map(items?.map((item) => [item.id, item]) ?? []).values());
}

export const ActionsSection = ({
	agent,
}: {
	agent: AgentDetailsWithAggData;
}): React.JSX.Element => {
	const { formatMessage } = useIntl();

	const { actions, mcpServers, mcpTools } = useMemo(() => {
		const scenarioTools =
			agent.scenarios
				?.filter(
					(scenario) =>
						scenario.isActive && scenario.isValid?.value && scenario.scenarioVersion === 2,
				)
				.flatMap((scenario) => scenario.tools ?? []) ?? [];

		// Get all actions from active scenarios only, then deduplicate by actionKey
		const scenarioActions =
			agent.scenarios
				?.filter((scenario) => scenario.isActive && scenario.isValid?.value)
				.flatMap((scenario) => scenario.actions ?? [])
				.map((action) => ({
					...action,
					id: action.actionKey,
					displayName: action.name,
				})) ?? [];

		return {
			actions: deduplicateById<AgentScenarioAction>([...scenarioActions, ...scenarioTools]),
			mcpServers: deduplicateById(agent.mcpServers),
			mcpTools: deduplicateById(agent.mcpTools),
		};
	}, [agent]);

	if (!actions.length && !mcpServers.length && !mcpTools.length) {
		return (
			<Text>
				{formatMessage(
					fg('agent_studio_atomic_skills_rename') ? messages.noTools : messages.noSkills,
				)}
			</Text>
		);
	}

	return (
		<Stack space="space.050">
			{actions.map((action) => (
				<AgentActionPill key={action.id} action={action} isActive={true} />
			))}
			{mcpServers.map((mcpServer) => (
				<ToolPill
					key={mcpServer.id}
					displayName={mcpServer.displayName}
					iconUrl={mcpServer.iconUrl}
				/>
			))}
			{mcpTools.map((mcpTool) => (
				<ToolPill key={mcpTool.id} displayName={mcpTool.displayName} iconUrl={mcpTool.iconUrl} />
			))}
		</Stack>
	);
};
